// src/pages/finance/payroll/SalaryHistory.jsx
// Salary History — single split-pane page: searchable/filterable employee
// list on the left, selected employee's monthly salary history + payslip
// "View" action on the right. Route: /finance/salary-history

import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/common/Layout";
import { getNavItems, getPanelLabel } from "../../../utils/navigation";
import { useAuth } from "../../../context/AuthContext";
import { useAllUsers, useCollection } from "../../../hooks/useFirestore";
import Payslip from "./Payslip";

/* ── DESIGN TOKENS — match PayrollSection / EmployeeDirectory ───────── */
const PRIMARY = "#1B4B31";
const PRIMARY_LIGHT = "#EAF2EE";
const BORDER = "#E2E8F0";
const WHITE = "#FFFFFF";
const SHADOW = "0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)";
const MUTED = "#9ca3af";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Pending shows as amber, Paid shows as green — matches the reference design.
const STATUS_STYLE = {
  Paid: { bg: "#DCFCE7", color: "#166534", label: "Paid" },
  "Pending Payment": { bg: "#FEF3C7", color: "#B45309", label: "Pending" },
  Pending: { bg: "#FEF3C7", color: "#B45309", label: "Pending" },
};

const EMPLOYEES_PER_PAGE = 6;
const RECORDS_PER_PAGE = 10;

/* ── small inline icons (no external icon package required) ─────────── */
function SearchIcon({ size = 16, color = MUTED }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CalendarIcon({ size = 16, color = PRIMARY }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function FilterIcon({ size = 15, color = WHITE }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function ResetIcon({ size = 15, color = "#374151" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function EyeIcon({ size = 14, color = PRIMARY }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function HomeIcon({ size = 14, color = "#6b7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5 12 3l9 6.5" />
      <path d="M5 10.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9.5" />
    </svg>
  );
}

/* ── helpers ──────────────────────────────────────────────────────── */
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#F3F4F6", color: "#374151", label: status || "—" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.25rem 0.7rem",
        borderRadius: "999px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

function isInternEmployee(emp) {
  if (!emp) return false;
  const label = (emp.category || emp.role || emp.designation || emp.employeeType || "")
    .toString()
    .toLowerCase();
  return label.includes("intern");
}

function employeeDisplayName(emp) {
  if (!emp) return "Unnamed employee";
  return emp.displayName || emp.name || emp.email || emp.id || "Unnamed employee";
}

function employeePhotoUrl(emp) {
  if (!emp) return null;
  return emp.photoURL || emp.photoUrl || emp.avatarUrl || emp.avatar || emp.profilePicture || null;
}

function initials(name) {
  return (name || "??")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
}

/* Circular avatar — shows the employee's photo when one is on file,
   otherwise falls back to an initials badge (same look, no dead images). */
function Avatar({ emp, size = 38, fontSize = "0.8rem" }) {
  const name = employeeDisplayName(emp);
  const photo = employeePhotoUrl(emp);
  const common = {
    width: `${size}px`,
    height: `${size}px`,
    minWidth: `${size}px`,
    borderRadius: "50%",
  };
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        style={{ ...common, objectFit: "cover", border: `1px solid ${BORDER}` }}
      />
    );
  }
  return (
    <div
      style={{
        ...common,
        background: PRIMARY,
        color: WHITE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize,
      }}
    >
      {initials(name)}
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  const millis = typeof ts.toMillis === "function"
    ? ts.toMillis()
    : typeof ts.seconds === "number"
    ? ts.seconds * 1000
    : new Date(ts).getTime();
  if (Number.isNaN(millis)) return "—";
  return new Date(millis).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function sortKey(p) {
  const monthIndex = MONTHS.indexOf(p.month);
  return Number(p.year || 0) * 100 + (monthIndex >= 0 ? monthIndex : 0);
}

function monthKey(year, monthLabel) {
  const idx = MONTHS.indexOf(monthLabel);
  return Number(year || 0) * 100 + (idx >= 0 ? idx : 0);
}

/* ── month/year picker — one bordered field with a calendar icon so the
   two dropdowns read as a single "June 2026" control ─────────────────── */
function MonthYearSelect({ label, monthValue, yearValue, onChange, years }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#374151", marginBottom: "0.4rem" }}>
        {label}
      </label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          border: `1px solid ${BORDER}`,
          borderRadius: "10px",
          padding: "0.55rem 0.75rem",
          background: WHITE,
        }}
      >
        <CalendarIcon />
        <select
          value={monthValue}
          onChange={(e) => onChange({ month: e.target.value, year: yearValue })}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: "0.9rem",
            color: "#111827",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {MONTHS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          value={yearValue}
          onChange={(e) => onChange({ month: monthValue, year: e.target.value })}
          style={{
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: "0.9rem",
            color: "#111827",
            fontWeight: 500,
            cursor: "pointer",
            width: "4.75rem",
          }}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* Search input with a leading magnifier icon */
function SearchInput({ value, onChange, placeholder, style }) {
  return (
    <div style={{ position: "relative", ...style }}>
      <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
        <SearchIcon />
      </span>
      <input
        className="filter-select"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ width: "100%", paddingLeft: "2.15rem" }}
      />
    </div>
  );
}

export default function SalaryHistory() {
  const { userProfile } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  const { data: users, loading: usersLoading } = useAllUsers();
  const { data: pendingPayments, loading: paymentsLoading } = useCollection("pendingPayments");

  const employees = useMemo(
    () => users.filter((u) => u.isActive !== false),
    [users]
  );

  /* ── left panel: search + pagination ─────────────────────────────── */
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeePage, setEmployeePage] = useState(1);

  const filteredEmployees = useMemo(() => {
    const term = employeeSearch.trim().toLowerCase();
    const list = !term
      ? employees
      : employees.filter((e) => {
          const name = employeeDisplayName(e).toLowerCase();
          const email = (e.email || "").toLowerCase();
          const empId = (e.employeeId || e.empCode || "").toLowerCase();
          return name.includes(term) || email.includes(term) || empId.includes(term);
        });
    return [...list].sort((a, b) => employeeDisplayName(a).localeCompare(employeeDisplayName(b)));
  }, [employees, employeeSearch]);

  const employeeTotalPages = Math.max(1, Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE));
  const pagedEmployees = useMemo(() => {
    const start = (employeePage - 1) * EMPLOYEES_PER_PAGE;
    return filteredEmployees.slice(start, start + EMPLOYEES_PER_PAGE);
  }, [filteredEmployees, employeePage]);

  useEffect(() => {
    setEmployeePage(1);
  }, [employeeSearch]);

  /* ── selected employee ────────────────────────────────────────────── */
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!selectedId && filteredEmployees.length > 0) {
      setSelectedId(filteredEmployees[0].id);
    }
  }, [filteredEmployees, selectedId]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId]
  );

  /* ── month range filters ──────────────────────────────────────────── */
  const currentDate = new Date();
  const yearOptions = useMemo(() => {
    const y = currentDate.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const [fromFilter, setFromFilter] = useState({
    month: MONTHS[currentDate.getMonth()],
    year: String(currentDate.getFullYear()),
  });
  const [toFilter, setToFilter] = useState({
    month: MONTHS[currentDate.getMonth()],
    year: String(currentDate.getFullYear()),
  });
  const [appliedRange, setAppliedRange] = useState(null); // null = no filter applied

  function applyFilters() {
    setAppliedRange({ from: fromFilter, to: toFilter });
    setRecordPage(1);
  }
  function resetFilters() {
    setAppliedRange(null);
    setRecordPage(1);
  }

  /* ── history for selected employee ───────────────────────────────── */
  const fullHistory = useMemo(
    () =>
      pendingPayments
        .filter((p) => p.employeeId === selectedId)
        .sort((a, b) => sortKey(b) - sortKey(a)),
    [pendingPayments, selectedId]
  );

  const history = useMemo(() => {
    if (!appliedRange) return fullHistory;
    const fromKey = monthKey(appliedRange.from.year, appliedRange.from.month);
    const toKey = monthKey(appliedRange.to.year, appliedRange.to.month);
    const lo = Math.min(fromKey, toKey);
    const hi = Math.max(fromKey, toKey);
    return fullHistory.filter((p) => {
      const k = monthKey(p.year, p.month);
      return k >= lo && k <= hi;
    });
  }, [fullHistory, appliedRange]);

  const [recordPage, setRecordPage] = useState(1);
  useEffect(() => setRecordPage(1), [selectedId]);

  const recordTotalPages = Math.max(1, Math.ceil(history.length / RECORDS_PER_PAGE));
  const pagedHistory = useMemo(() => {
    const start = (recordPage - 1) * RECORDS_PER_PAGE;
    return history.slice(start, start + RECORDS_PER_PAGE);
  }, [history, recordPage]);

  const [payslipFor, setPayslipFor] = useState(null);

  const loading = usersLoading || paymentsLoading;

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", color: "#6b7280", marginBottom: "0.5rem" }}>
          <HomeIcon />
          <span style={{ marginLeft: "0.4rem" }}>Finance</span>
          <span style={{ margin: "0 0.3rem" }}>›</span>
          <span style={{ color: "#111827", fontWeight: 600 }}>Salary History</span>
        </div>
        <h2 style={{ margin: 0 }}>Salary History</h2>
        <p style={{ margin: "0.25rem 0 0", color: "var(--text-secondary, #666)" }}>
          View monthly salary details and payslips of employees.
        </p>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────── */}
      <div
        className="form-card card"
        style={{
          padding: "1.25rem 1.5rem",
          marginBottom: "1.25rem",
          display: "flex",
          gap: "1.25rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 220px", minWidth: "220px" }}>
          <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#374151", marginBottom: "0.4rem" }}>
            Employee
          </label>
          <SearchInput
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
            placeholder="Search employee…"
          />
        </div>

        <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
          <MonthYearSelect
            label="From Month"
            monthValue={fromFilter.month}
            yearValue={fromFilter.year}
            years={yearOptions}
            onChange={setFromFilter}
          />
        </div>

        <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
          <MonthYearSelect
            label="To Month"
            monthValue={toFilter.month}
            yearValue={toFilter.year}
            years={yearOptions}
            onChange={setToFilter}
          />
        </div>

        <div style={{ display: "flex", gap: "0.6rem" }}>
          <button
            className="btn-primary"
            onClick={applyFilters}
            style={{
              background: PRIMARY,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "0.45rem",
            }}
          >
            <FilterIcon />
            Apply Filters
          </button>
          <button
            className="btn-ghost-sm"
            onClick={resetFilters}
            style={{
              padding: "0.55rem 1rem",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            <ResetIcon />
            Reset
          </button>
        </div>
      </div>

      {/* ── Split pane ───────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.25rem", alignItems: "start" }}>
        {/* Left: employee list */}
        <div className="form-card card" style={{ padding: "1.25rem" }}>
          <h3 className="form-card-title" style={{ margin: "0 0 0.75rem" }}>Employee List</h3>
          <SearchInput
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
            placeholder="Search employee…"
            style={{ marginBottom: "0.9rem" }}
          />

          {loading ? (
            <p>Loading…</p>
          ) : pagedEmployees.length === 0 ? (
            <div className="card-empty" style={{ padding: "1.5rem 0.5rem", textAlign: "center" }}>
              <p>No employees match this search.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {pagedEmployees.map((emp) => {
                const name = employeeDisplayName(emp);
                const isSelected = emp.id === selectedId;
                return (
                  <div
                    key={emp.id}
                    onClick={() => setSelectedId(emp.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelectedId(emp.id);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.7rem",
                      padding: "0.65rem 0.75rem",
                      borderRadius: "10px",
                      cursor: "pointer",
                      background: isSelected ? PRIMARY_LIGHT : "transparent",
                      border: `1px solid ${isSelected ? PRIMARY : "transparent"}`,
                    }}
                  >
                    <Avatar emp={emp} size={38} fontSize="0.8rem" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "#111827", fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {name}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        {(emp.employeeId || emp.empCode) ? `${emp.employeeId || emp.empCode} | ` : ""}
                        {emp.department || (isInternEmployee(emp) ? "Intern" : "Employee")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredEmployees.length > 0 && (
            <>
              <p style={{ fontSize: "0.78rem", color: "#9ca3af", margin: "0.9rem 0 0.5rem" }}>
                Showing {(employeePage - 1) * EMPLOYEES_PER_PAGE + 1} to{" "}
                {Math.min(employeePage * EMPLOYEES_PER_PAGE, filteredEmployees.length)} of {filteredEmployees.length} employees
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "0.35rem", flexWrap: "wrap" }}>
                <button
                  className="btn-ghost-sm"
                  disabled={employeePage === 1}
                  onClick={() => setEmployeePage((p) => Math.max(1, p - 1))}
                  style={{ padding: "0.25rem 0.6rem" }}
                >
                  ‹
                </button>
                {Array.from({ length: employeeTotalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setEmployeePage(n)}
                    className="btn-ghost-sm"
                    style={{
                      padding: "0.25rem 0.6rem",
                      background: n === employeePage ? PRIMARY : "transparent",
                      color: n === employeePage ? WHITE : undefined,
                      borderColor: n === employeePage ? PRIMARY : BORDER,
                    }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  className="btn-ghost-sm"
                  disabled={employeePage === employeeTotalPages}
                  onClick={() => setEmployeePage((p) => Math.min(employeeTotalPages, p + 1))}
                  style={{ padding: "0.25rem 0.6rem" }}
                >
                  ›
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: selected employee details + history */}
        <div className="form-card card" style={{ padding: "1.5rem" }}>
          {loading ? (
            <p>Loading…</p>
          ) : !selectedEmployee ? (
            <div className="card-empty" style={{ padding: "2rem", textAlign: "center" }}>
              <p>Select an employee to view their salary history.</p>
            </div>
          ) : (
            <>
              {/* Employee summary */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                <Avatar emp={selectedEmployee} size={56} fontSize="1.1rem" />

                <div style={{ flex: 1, minWidth: "260px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0 }}>{employeeDisplayName(selectedEmployee)}</h3>
                    {(selectedEmployee.employeeId || selectedEmployee.empCode) && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: PRIMARY,
                          background: PRIMARY_LIGHT,
                          padding: "0.15rem 0.55rem",
                          borderRadius: "999px",
                        }}
                      >
                        {selectedEmployee.employeeId || selectedEmployee.empCode}
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      rowGap: "0.4rem",
                      columnGap: "1rem",
                      marginTop: "0.6rem",
                      fontSize: "0.85rem",
                      color: "#374151",
                    }}
                  >
                    <div><strong>Department</strong> &nbsp;: &nbsp;{selectedEmployee.department || "—"}</div>
                    <div><strong>Designation</strong> &nbsp;: &nbsp;{selectedEmployee.designation || selectedEmployee.role || "—"}</div>
                    <div><strong>Date of Joining</strong> &nbsp;: &nbsp;{formatDate(selectedEmployee.dateOfJoining || selectedEmployee.joiningDate)}</div>
                    <div><strong>Employment Type</strong> &nbsp;: &nbsp;{selectedEmployee.employmentType || (isInternEmployee(selectedEmployee) ? "Intern" : "Full Time")}</div>
                    <div><strong>PAN Number</strong> &nbsp;: &nbsp;{selectedEmployee.panNumber || "—"}</div>
                    <div>
                      <strong>Bank Account</strong> &nbsp;: &nbsp;
                      {selectedEmployee.bankAccountNumber
                        ? `${selectedEmployee.bankAccountNumber}${selectedEmployee.bankName ? ` (${selectedEmployee.bankName})` : ""}`
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>

              <hr style={{ border: "none", borderTop: `1px solid ${BORDER}`, margin: "0 0 1.25rem" }} />

              {/* History table */}
              <h3 className="form-card-title" style={{ margin: "0 0 0.9rem" }}>Monthly Salary History</h3>

              {history.length === 0 ? (
                <div className="card-empty" style={{ padding: "2rem", textAlign: "center" }}>
                  <p>No salary records found for this employee{appliedRange ? " in the selected range" : ""}.</p>
                </div>
              ) : (
                <>
                  <div className="data-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Month &amp; Year</th>
                          <th>Basic Salary (₹)</th>
                          <th>Total Earnings (₹)</th>
                          <th>Total Deductions (₹)</th>
                          <th>Net Payable (₹)</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedHistory.map((p) => (
                          <tr key={p.id}>
                            <td>{p.month} {p.year}</td>
                            <td>{Number(p.basic || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td>{Number(p.gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td>{Number(p.deductions || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td style={{ fontWeight: 600 }}>{Number(p.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td><StatusPill status={p.status} /></td>
                            <td>
                              <button
                                className="btn-ghost-sm"
                                onClick={() => setPayslipFor(p)}
                                style={{
                                  padding: "0.3rem 0.75rem",
                                  fontSize: "0.8rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                              >
                                <EyeIcon />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "0.75rem",
                      marginTop: "0.9rem",
                    }}
                  >
                    <p style={{ fontSize: "0.78rem", color: "#9ca3af", margin: 0 }}>
                      Showing {(recordPage - 1) * RECORDS_PER_PAGE + 1} to{" "}
                      {Math.min(recordPage * RECORDS_PER_PAGE, history.length)} of {history.length} records
                    </p>
                    <div style={{ display: "flex", gap: "0.35rem" }}>
                      <button
                        className="btn-ghost-sm"
                        disabled={recordPage === 1}
                        onClick={() => setRecordPage((p) => Math.max(1, p - 1))}
                        style={{ padding: "0.25rem 0.6rem" }}
                      >
                        ‹
                      </button>
                      {Array.from({ length: recordTotalPages }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          onClick={() => setRecordPage(n)}
                          className="btn-ghost-sm"
                          style={{
                            padding: "0.25rem 0.6rem",
                            background: n === recordPage ? PRIMARY : "transparent",
                            color: n === recordPage ? WHITE : undefined,
                            borderColor: n === recordPage ? PRIMARY : BORDER,
                          }}
                        >
                          {n}
                        </button>
                      ))}
                      <button
                        className="btn-ghost-sm"
                        disabled={recordPage === recordTotalPages}
                        onClick={() => setRecordPage((p) => Math.min(recordTotalPages, p + 1))}
                        style={{ padding: "0.25rem 0.6rem" }}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {payslipFor && (
        <Payslip
          payment={payslipFor}
          employee={selectedEmployee}
          onClose={() => setPayslipFor(null)}
        />
      )}
    </Layout>
  );
}